import {
  Button,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ChangePassword,
  GetChangePassword,
  SendEmailChangePassword,
  SendSMSChangePassword,
} from "store/api/changePassword";
import { useEffect, useState } from "react";

import CountdownButton from "components/CountdownButton";
import { LoadingButton } from "@mui/lab";
import PasswordField from "components/PasswordField";
import { allowMaxCharLength } from "utils/helpers/allowMaxCharLength";
import { changePasswordSchema } from "utils/validations/changePasswordSchema";
import http from "config/http";
import { logout } from "config/authentication";
import { useForm } from "react-hook-form";
import useNotify from "hooks/useNotify";
import { yupResolver } from "@hookform/resolvers/yup";
import { actionButtonBg } from "utils/helpers/actionButtonBg";

function ChangePasswordForm({ onClose }) {
  const notify = useNotify();
  const theme = useTheme()
  
  const [loading, setLoading] = useState(false);
  const [changePasswordDetails, setChangePasswordDetails] = useState(null);

  const {
    handleSubmit,
    watch,
    setValue,
    reset,
    trigger,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      emailCode: "",
      smsCode: "",
      appCode: "",
      newPassword: "",
      oldPassword: "",
      confirmPassword: "",
      isShowSMS: changePasswordDetails?.isShowSMS,
      isShowApp: changePasswordDetails?.isShowApp,
    },
    resolver: yupResolver(changePasswordSchema()),
  });

  const getChangePassword = async () => {
    try {
      const { data } = await http.get(GetChangePassword);
      setChangePasswordDetails(data?.data);
      reset({
        emailCode: "",
        smsCode: "",
        appCode: "",
        newPassword: "",
        oldPassword: "",
        confirmPassword: "",
        isShowSMS: data?.data?.isShowSMS,
        isShowApp: data?.data?.isShowApp,
      });
    } catch (e) {
      notify(e?.response?.data?.message, "error");
    }
  };

  const handleGetEmailPassword = async (startCountDown) => {
    try {
      await http.get(SendEmailChangePassword);

      startCountDown();
    } catch (e) {
      notify(e?.response?.data?.message, "error");
    }
  };

  const handleGetSmsCode = async (startCountDown) => {
    try {
      await http.get(SendSMSChangePassword);

      startCountDown();
    } catch (e) {
      notify(e?.response?.data?.message, "error");
    }
  };

  const onSubmit = async (data) => {
    const body = {
      appCode: data.appCode,
      smsCode: data.smsCode,
      confirmPassword: data.confirmPassword,
      emailCode: data.emailCode,
      newPassword: data.newPassword,
      oldPassword: data.oldPassword,
    };

    try {
      setLoading(true);

      await http.post(ChangePassword, body);
      logout();
    } catch (e) {
      notify(e?.response?.data?.message, "error");
    }

    setLoading(false);
  };

  useEffect(() => {
    getChangePassword();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2}>
        <Grid item xs={12} textAlign="right">
          <Typography variant="label">رمز عبور فعلی</Typography>

          <PasswordField
            placeholder="رمز عبور فعلی خود را وارد کنید"
            error={!!errors?.oldPassword}
            helperText={errors?.oldPassword?.message}
            value={watch("oldPassword")}
            onChange={async (e) => {
              setValue("oldPassword", e.target.value);
              await trigger("oldPassword");
            }}
          />
        </Grid>

        <Grid item xs={12} textAlign="right">
          <Typography variant="label">رمز عبور جدید</Typography>

          <PasswordField
            placeholder="رمز عبور جدید را وارد کنید"
            error={!!errors?.newPassword}
            helperText={errors?.newPassword?.message}
            value={watch("newPassword")}
            onChange={async (e) => {
              setValue("newPassword", e.target.value);
              await trigger("newPassword");
            }}
          />
        </Grid>

        <Grid item xs={12} textAlign="right">
          <Typography variant="label">تایید رمز عبور</Typography>

          <PasswordField
            placeholder="رمز عبور جدید را دوباره وارد کنید"
            error={!!errors?.confirmPassword}
            helperText={errors?.confirmPassword?.message}
            value={watch("confirmPassword")}
            onChange={async (e) => {
              setValue("confirmPassword", e.target.value);
              await trigger("confirmPassword");
            }}
          />
        </Grid>

        <Grid item xs={12} textAlign="right" mt={2}>
          <Typography variant="label" dir="rtl">
            ایمیل: {changePasswordDetails?.email}
          </Typography>

          <TextField
            dir="rtl"
            placeholder="کد ارسالی به ایمیل خود را وارد کنید "
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <CountdownButton
                    time={changePasswordDetails?.validSecondsSendEmail}
                    onClick={handleGetEmailPassword}
                  />
                </InputAdornment>
              ),
            }}
            error={!!errors?.emailCode}
            helperText={errors?.emailCode?.message}
            value={watch("emailCode")}
            onChange={async (e) => {
              allowMaxCharLength({
                name: "emailCode",
                value: e.target.value,
                setValue,
                length: 6,
              });
              await trigger("emailCode");
            }}
          />
        </Grid>

        {changePasswordDetails?.isShowSMS && (
          <Grid item xs={12} textAlign="right">
            <Typography variant="label">
              Phone: {changePasswordDetails?.phoneNumber}
            </Typography>

            <TextField
              dir="rtl"
              placeholder="کد SMS ارسالی را وارد کنید"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CountdownButton
                      time={changePasswordDetails?.validSecondsSendSMS}
                      onClick={handleGetSmsCode}
                    />
                  </InputAdornment>
                ),
              }}
              error={!!errors?.smsCode}
              helperText={errors?.smsCode?.message}
              value={watch("smsCode")}
              onChange={async (e) => {
                allowMaxCharLength({
                  name: "smsCode",
                  value: e.target.value,
                  setValue,
                  length: 6,
                });
                await trigger("smsCode");
              }}
            />
          </Grid>
        )}

        {changePasswordDetails?.isShowApp && (
          <Grid item xs={12} textAlign="right">
            <TextField
              dir="rtl"
              placeholder="کد Authentication App را وارد کنید"
              error={!!errors?.appCode}
              helperText={errors?.appCode?.message}
              value={watch("appCode")}
              onChange={async (e) => {
                allowMaxCharLength({
                  name: "appCode",
                  value: e.target.value,
                  setValue,
                  length: 6,
                });

                await trigger("appCode");
              }}
            />
          </Grid>
        )}

        <Grid item xs={6}>
          <Button color="info" fullWidth onClick={onClose}>
            انصراف
          </Button>
        </Grid>

        <Grid item xs={6}>
          <LoadingButton
            variant="contained"
            loading={loading}
            fullWidth
            color={actionButtonBg(theme)}
            type="submit"
          >
            تغییر رمز عبور
          </LoadingButton>
        </Grid>
      </Grid>
    </form>
  );
}

export default ChangePasswordForm;
